import React, { useState, useEffect, useRef, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { appUrl } from '../../../../../utils/config';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import StyledDialog from '../../../../common/models/Dialog';
import EventEditViewDialog from './EventEditViewDialog';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Avatar from '@material-ui/core/Avatar';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import Backdrop from '@material-ui/core/Backdrop';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CreateIcon from '@material-ui/icons/Create';
import CloseIcon from '@material-ui/icons/Close';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PublishIcon from '@material-ui/icons/Publish';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import * as XLSX from 'xlsx';
import {
  fetchDistribution,
  updateDistribution,
  clearDistributionState,
  fetchOrgDeptRoles,
} from '../../../containers/actions/common/notificationsAction';

const ORANGE = '#e36e39';

// Categories are sorted alphabetically (A → Z) across all views.

// ─── styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 1.5),
    },
  },

  // ── back button ────────────────────────────────────────────────────────────
  backBtn: {
    textTransform: 'none',
    fontSize: '0.875rem',
    color: '#333',
    padding: '2px 6px 2px 0',
    minWidth: 0,
    marginBottom: theme.spacing(1),
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },

  // ── title + action buttons row ────────────────────────────────────────────
  titleActionRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
  },
  titleSection: {},
  title: {
    fontWeight: 500,
    fontSize: '1.5rem',
    textAlign: 'left',
    paddingBottom: '0.3rem',
  },
  titleBorder: {
    border: '2px solid #707070',
    display: 'flex',
    width: '1.6rem',
    backgroundColor: 'grey',
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  outlinedBtn: {
    textTransform: 'none',
    fontSize: '0.8rem',
    color: '#333',
    borderColor: '#bbb',
    borderRadius: '2rem',
    padding: '4px 14px',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: '#333',
      backgroundColor: 'transparent',
    },
  },

  // ── tabs row ───────────────────────────────────────────────────────────────
  tabsRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  tabsRoot: {
    '& .MuiTab-root:first-child': {
      paddingLeft: 0,
    },
  },
  tabsIndicator: {
    display: 'none',
  },
  tab: {
    textTransform: 'none',
    fontSize: '0.875rem',
    minWidth: 'auto',
    padding: '8px 16px',
    fontWeight: 400,
    '&.Mui-selected': {
      color: ORANGE,
      fontWeight: 'bold',
    },
    '&.Mui-selected .MuiTab-wrapper': {
      borderBottom: `2px solid ${ORANGE}`,
      paddingBottom: 2,
    },
  },
  lastUpdated: {
    fontSize: '0.75rem',
    color: '#888',
    whiteSpace: 'nowrap',
    paddingBottom: 8,
  },

  // ── table ──────────────────────────────────────────────────────────────────
  tableWrapper: {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 320px)',
    width: '100%',
  },
  tablePaper: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  tableHead: {
    backgroundColor: '#fafafa',
    '& .MuiTableCell-head': {
      fontWeight: 'bold',
      fontSize: '0.875rem',
      color: '#333',
      borderBottom: '2px solid #e0e0e0',
      position: 'sticky',
      top: 0,
      backgroundColor: '#fafafa',
      zIndex: 1,
    },
  },
  tableRow: {
    '&:hover': { backgroundColor: '#f9f9f9' },
  },
  tableCell: {
    fontSize: '0.875rem',
    color: '#333',
    padding: '10px 16px',
  },
  actionCell: {
    whiteSpace: 'nowrap',
    padding: '10px 16px',
  },
  updatedByCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    whiteSpace: 'nowrap',
  },
  avatar: {
    width: 28,
    height: 28,
    fontSize: '0.7rem',
    backgroundColor: '#e36e39',
  },
  actionIconBtn: {
    border: '1px solid #7d7c7c',
    borderRadius: '4px',
    padding: '0.3rem',
    height: 'inherit',
    marginLeft: '0.5rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'black',
      color: 'white',
      border: '1px solid #fff',
    },
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },

  // ── CMO download dialog ────────────────────────────────────────────────────
  cmoDialogPaper: {
    borderRadius: 8,
    padding: '1rem 1.5rem 1.5rem',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  cmoDialogCancel: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: '0.25rem',
  },
  cancelBtn: {
    textTransform: 'none',
    fontSize: '0.875rem',
    color: '#333',
    fontWeight: 500,
    padding: '2px 0',
    minWidth: 0,
    '&:hover': { backgroundColor: 'transparent' },
  },
  cancelIcon: {
    color: '#e53935',
    fontSize: '1.2rem',
  },
  cmoDialogTitle: {
    fontWeight: 500,
    fontSize: '1.5rem',
    textAlign: 'center',
    marginBottom: '0.5rem',
    color: '#111',
    lineHeight: 1.3,
  },
  cmoDialogSubtitle: {
    fontSize: '1rem',
    textAlign: 'center',
    color: '#555',
    marginBottom: '1.25rem',
  },
  cmoFormControl: {
    width: '100%',
    marginBottom: '1.5rem',
    '& .MuiSelect-outlined': {
      padding: '10px 14px',
      fontSize: '0.875rem',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 4,
    },
  },
  cmoLabel: {
    fontWeight: 600,
    fontSize: '0.8125rem',
    color: '#333',
    marginBottom: '0.35rem',
  },
  requiredAsterisk: {
    color: '#e53935',
  },
  confirmBtn: {
    display: 'flex',
    margin: '0 auto',
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 600,
    backgroundColor: ORANGE,
    color: '#fff',
    borderRadius: '2rem',
    padding: '7px 24px',
    '&:hover': {
      backgroundColor: '#c95c2b',
    },
    '&:disabled': {
      backgroundColor: '#e0a080',
      color: '#fff',
    },
  },

  // ── upload org-type dialog ─────────────────────────────────────────────────
  uploadDialogPaper: {
    borderRadius: 8,
    padding: '1rem 1.5rem 1.5rem',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },

  // ── upload file dialog ─────────────────────────────────────────────────────
  uploadBox: {
    width: '100%',
    marginTop: '2em',
    border: '1px solid black',
    backgroundColor: '#f5f5f5',
    padding: '2em 0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    minHeight: 60,
  },
  uploadText: {
    marginLeft: 'auto',
    textAlign: 'center',
    flex: 1,
    fontSize: 16,
  },
  uploadIcon: {
    fontSize: '40px',
    marginLeft: 'auto',
    marginRight: '20px',
    color: '#333',
  },
  file: {
    marginTop: '2em',
    display: 'flex',
    flexDirection: 'column',
    gap: '1em',
    alignItems: 'flex-start',
  },
  link: {
    color: 'blue',
    textDecoration: 'underline',
  },
  linkBox: {
    cursor: 'pointer',
    display: 'flex',
    gap: '0.5em',
  },
  bold: {
    fontWeight: 500,
  },
  criteria: {
    maxWidth: '500px',
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#ec6535',
  },
  uploadBtn: {
    backgroundColor: '#ec6535',
    color: 'white',
    textTransform: 'none',
    borderRadius: '50px',
    margin: '1em auto',
    padding: '5px 30px',
    '&:hover': {
      backgroundColor: '#ec6535',
    },
    '&:disabled': { 
      backgroundColor: '#e0a080', 
      color: '#fff' 
    },
  },

  // ── success dialog ─────────────────────────────────────────────────────────
  successDialogPaper: {
    borderRadius: 8,
    padding: '0.5rem',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  successContent: {
    textAlign: 'center',
    padding: '2rem 2rem 2.5rem',
  },
  successIcon: {
    fontSize: '5rem',
    color: '#39811D',
    marginBottom: '0.8rem',
  },
  successTitle: {
    fontWeight: 500,
    fontSize: '1.5rem',
    color: '#111',
    marginBottom: '0.4rem',
  },
  successSubtitle: {
    fontSize: '1rem',
    color: '#666',
    marginBottom: '1.8rem',
  },
  okBtn: {
    textTransform: 'none',
    fontSize: '0.95rem',
    fontWeight: 600,
    backgroundColor: ORANGE,
    color: '#fff',
    borderRadius: '2rem',
    padding: '8px 36px',
    '&:hover': {
      backgroundColor: '#c95c2b',
    },
  },
  validationDialogPaper: {
    borderRadius: '12px',
    maxWidth: '600px',
  },
  validationDialogContent: {
    padding: '3rem',
  },
  validationTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#111',
    marginBottom: '1rem',
    textAlign: 'center',
  },
  validationSubtitle: {
    fontSize: '1rem',
    color: '#666',
    marginBottom: '2rem',
    textAlign: 'center',
  },
  summaryBox: {
    border: '2px solid #707070',
    padding: '1.5rem',
    textAlign: 'center',
    borderRadius: '8px',
  },
  summaryNumber: {
    fontSize: '2.4rem',
    fontWeight: 500,
    marginBottom: '0.5rem',
  },
  summaryLabel: {
    fontSize: '1rem',
    color: '#666',
  },
  validationActions: {
    marginTop: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  reUploadBtn: {
    textDecoration: 'underline',
    textTransform: 'none',
    color: '#666',
    cursor: 'pointer',
    '&:hover': {
      color: '#333',
    },
  },
  submitBtn: {
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 600,
    backgroundColor: ORANGE,
    color: '#fff',
    borderRadius: '2rem',
    padding: '8px 36px',
    '&:hover': {
      backgroundColor: '#c95c2b',
    },
  },
}));

// Acronyms that must always render in full caps in event names / labels.
const EVENT_NAME_ACRONYMS = new Set([
  'BMS', 'CMO', 'ABO', 'PO', 'EU', 'ASN', 'SAP', 'API',
  'GRN', 'PCMP', 'ERP', 'UOM', 'QC', 'QA', 'CC', 'SL', 'ID',
]);

const formatEventName = (name) => {
  if (!name) return '';
  return name
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')   // "CMOUser" -> "CMO User"
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')        // "batchAllocation" -> "batch Allocation"
    .replace(/[_-]+/g, ' ')                          // snake_case / kebab -> spaces
    .trim()
    .split(/\s+/)
    .map((w) => {
      const upper = w.toUpperCase();
      if (EVENT_NAME_ACRONYMS.has(upper)) return upper;            // BMS, CMO, PO, EU, ASN...
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(); // Title Case each word
    })
    .join(' ');
};

// ─── component ────────────────────────────────────────────────────────────────

const ManageDistributionList = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const {
    distribution,
    distributionLoading,
    uploadDistributionSuccess,
    updateDistributionSuccess,
    updateDistributionError,
    orgDeptRoles,
  } = useSelector((state) => state.pcmp.common.notifications);

  const [distributionSaving, setDistributionSaving] = useState(false);

  // ── Snackbar state (success/error feedback on save) ──────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const [activeTab, setActiveTab] = useState(0);

  // ── event view / edit dialog ───────────────────────────────────────────────
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventDialogMode, setEventDialogMode] = useState('view'); // 'view' | 'edit'
  const [selectedEvent, setSelectedEvent] = useState(null);

  // ── download CMO dialog ────────────────────────────────────────────────────
  const [cmoDialogOpen, setCmoDialogOpen] = useState(false);
  const [selectedCmo, setSelectedCmo] = useState('');

  // ── Excel preview dialog ──────────────────────────────────────────────────
  const [excelPreviewOpen, setExcelPreviewOpen] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [excelFileURL, setExcelFileURL] = useState(null);
  const [excelFileName, setExcelFileName] = useState('');

  // ── upload dialog (step 1: org type, step 2: file, step 3: validation) ─────
  const [uploadOrgDialogOpen, setUploadOrgDialogOpen] = useState(false);
  const [selectedOrgType, setSelectedOrgType] = useState('');
  const [uploadFileDialogOpen, setUploadFileDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [fileType, setFileType] = useState('.xlsx');
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // ── CMO selection for upload ───────────────────────────────────────────────
  const [selectedUploadCmo, setSelectedUploadCmo] = useState('');
  const [selectedUploadCmoId, setSelectedUploadCmoId] = useState('');

  // ── validation and submission ──────────────────────────────────────────────
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  // ── upload error message ───────────────────────────────────────────────────
  const [uploadError, setUploadError] = useState('');

  // ── Fetch distribution on mount ─────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchDistribution());
    if (!orgDeptRoles) dispatch(fetchOrgDeptRoles());
  }, [dispatch]);

  // ── Reset CMO upload selection when org type changes ─────────────────────────
  useEffect(() => {
    setSelectedUploadCmo('');
    setSelectedUploadCmoId('');
  }, [selectedOrgType]);

  // ── Re-fetch helper ─────────────────────────────────────────────────────────
  const refetchDistribution = () => {
    dispatch(fetchDistribution());
  };

  // ── React to update success ────────────────────────────────────────────────
  useEffect(() => {
    if (updateDistributionSuccess) {
      setDistributionSaving(false);
      setEventDialogOpen(false);
      refetchDistribution();
      setSnackbar({
        open: true,
        message: 'Distribution list updated successfully.',
        severity: 'success',
      });
      dispatch(clearDistributionState());
    }
  }, [updateDistributionSuccess, dispatch]);

  useEffect(() => {
    if (updateDistributionError) {
      setDistributionSaving(false);
      setSnackbar({
        open: true,
        message: 'Failed to update distribution list. Please try again.',
        severity: 'error',
      });
      dispatch(clearDistributionState());
    }
  }, [updateDistributionError, dispatch]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const handleOpenEventDialog = (ev, mode) => {
    setSelectedEvent(ev);
    setEventDialogMode(mode);
    setEventDialogOpen(true);
  };

  const handleOpenCmoDialog = () => {
    setSelectedCmo('');
    setCmoDialogOpen(true);
  };

  const handleCloseCmoDialog = () => setCmoDialogOpen(false);

  const [downloadLoading, setDownloadLoading] = useState(false);

  const getBase = () =>
    `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

  const handleDownloadAndPreview = async (params) => {
    setDownloadLoading(true);
    try {
      const res = await axios.post(`${getBase()}/downloadEventNotifications`, params);
      if (res.data && res.data.status && res.data.fileData) {
        const { fileData, fileName } = res.data;
        const byteArray = new Uint8Array(fileData.data);

        // Create download URL
        const blob = new Blob([byteArray], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);

        // Parse with XLSX for preview
        const workbook = XLSX.read(byteArray, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

        setExcelFileURL(url);
        setExcelFileName(fileName || 'EventRoleMapping.xlsx');
        setExcelData(jsonData);
        setExcelPreviewOpen(true);
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleConfirmDownload = () => {
    setCmoDialogOpen(false);
    handleDownloadAndPreview({
      collaboratorTypeID: 2,
      vendorMosymphonyId: selectedCmo,
    });
  };

  const handleDownloadBms = () => {
    handleDownloadAndPreview({
      collaboratorTypeID: 1,
    });
  };

  const handleCloseExcelPreview = () => {
    setExcelPreviewOpen(false);
    if (excelFileURL) {
      URL.revokeObjectURL(excelFileURL);
      setExcelFileURL(null);
    }
    setExcelData([]);
  };

  // ── Upload handlers ────────────────────────────────────────────────────────
  const handleOpenUploadOrgDialog = () => {
    setSelectedOrgType('');
    setUploadOrgDialogOpen(true);
  };
  const handleCloseUploadOrgDialog = () => setUploadOrgDialogOpen(false);

  const handleConfirmOrgType = () => {
    setUploadOrgDialogOpen(false);
    setUploadFile(null);
    setFileType('.xlsx');
    setUploadFileDialogOpen(true);
  };
  const handleCloseUploadFileDialog = () => {
    setUploadFileDialogOpen(false);
    setSelectedUploadCmo('');
    setSelectedUploadCmoId('');
    setUploadError('');
  };

  const handleUploadBoxClick = () => fileInputRef.current && fileInputRef.current.click();
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped) { setUploadFile(dropped); setUploadError(''); }
  };
  const handleFileChange = (e) => {
    if (e.target.files[0]) { setUploadFile(e.target.files[0]); setUploadError(''); }
  };
  const handleRemoveFile = () => {
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!uploadFile || isUploading) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (selectedOrgType === 'CMO') {
        formData.append('collaboratorTypeID', '2');
        if (selectedUploadCmoId) {
          formData.append('vendorMosymphonyID', selectedUploadCmoId);
        }
      } else {
        formData.append('collaboratorTypeID', '1');
      }

      const API_BASE_URL = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;
      const response = await axios.post(`${API_BASE_URL}/uploadEventRoleMapping`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });


      const data = response.data;

      if (data.status && data.validRecords) {
        setUploadError('');
        // Store validation data
        setValidationData(data);
        // Close file upload dialog and show validation summary
        setUploadFileDialogOpen(false);
        setValidationDialogOpen(true);
      } else {
        setUploadError(data.message || 'Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Upload API Error:', error);
      const errMsg = error?.response?.data?.errorDescription || error?.message || 'An error occurred during upload.';
      setUploadError(errMsg);
    } finally {
      setIsUploading(false);
    }
  };

  // ── Save from EventEditViewDialog ─────────────────────────────────────────
  const handleSaveDistribution = (data) => {
    setDistributionSaving(true);
    dispatch(updateDistribution(data));
  };

  // ── Submit valid records ───────────────────────────────────────────────────
  const handleSubmitValidRecords = async () => {
    if (!validationData?.validRecords || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const API_BASE_URL = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;
      const response = await axios.post(`${API_BASE_URL}/addEventNotificationToRoleMapping`, {
        validRecords: validationData.validRecords,
        collaboratorTypeID: selectedOrgType === 'CMO' ? 2 : 1,
        ...(selectedOrgType === 'CMO' && selectedUploadCmoId ? {
          vendorMosymphonyID: selectedUploadCmoId,
        } : {}),
      });



      if (response.data.status) {
        setValidationDialogOpen(false);
        setSubmissionSuccess(true);
        // Refresh distribution list
        refetchDistribution();
      } else {
        // alert(response.data.errorDescription || 'Submission failed.');
      }
    } catch (error) {
      console.error('Submit API Error:', error);
      const errMsg = error?.response?.data?.errorDescription || error?.message || 'An error occurred during submission.';
      // alert(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Handle re-upload ───────────────────────────────────────────────────────
  const handleReUpload = () => {
    setValidationDialogOpen(false);
    setValidationData(null);
    setUploadFile(null);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadFileDialogOpen(true);
  };

  // ── Close submission success dialog ────────────────────────────────────────
  const handleCloseSubmissionSuccess = () => {
    setSubmissionSuccess(false);
    setValidationData(null);
    setUploadFile(null);
    setSelectedOrgType('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isCmoUpload = selectedOrgType.startsWith('CMO');
  const uploadDialogTitle = isCmoUpload
    ? "Upload CMO Roles' Access To Events"
    : "Upload BMS Roles' Access To Events";
  const uploadDialogSubtitle = isCmoUpload
    ? 'Add / update the access of CMO roles to the different events'
    : 'Add / update the access of BMS roles to the different events';

  // ── Build sorted categories from API response ──────────────────────────────
  const categories = useMemo(() => {
    if (!Array.isArray(distribution) || !distribution.length) return [];
    // Sort alphabetically (A → Z), with "Others" always pinned last
    return [...distribution]
      .sort((a, b) => {
        if (a.objectType === 'Others') return 1;
        if (b.objectType === 'Others') return -1;
        return (a.objectType || '').localeCompare(b.objectType || '');
      })
      .map((group) => ({
        label: group.objectType,
        count: Array.isArray(group.eventNotifications) ? group.eventNotifications.length : 0,
      }));
  }, [distribution]);

  // Reset tab if categories shrink below current index
  useEffect(() => {
    if (categories.length && activeTab >= categories.length) {
      setActiveTab(0);
    }
  }, [categories.length, activeTab]);

  // ── Events for the active tab ─────────────────────────────────────────────
  const activeGroup = Array.isArray(distribution) ? distribution.find(
    (g) => g.objectType === (categories[activeTab] && categories[activeTab].label)
  ) : null;
  const filteredDistribution = activeGroup && Array.isArray(activeGroup.eventNotifications)
    ? activeGroup.eventNotifications
    : [];

  // CMO organizations for the download dropdown (from orgDeptRoles API)
  const cmoOptions = useMemo(() => {
    const orgs = orgDeptRoles?.organizations || [];
    return orgs
      .filter((o) => o.orgType === 'CMO' && o.organization_name)
      .map((o) => ({ name: o.organization_name, id: o.organization_id }));
  }, [orgDeptRoles]);

  return (
    <div className={classes.root}>

      {/* ── back button ───────────────────────────────────────────────────── */}
      <Button
        className={classes.backBtn}
        disableRipple
        startIcon={<KeyboardArrowLeftIcon style={{ fontSize: '1.5rem', marginRight: -2 }} />}
        onClick={() => history.goBack()}
      >
        Back
      </Button>

      {/* ── title + upload/download buttons ───────────────────────────────── */}
      <div className={classes.titleActionRow}>
        <div className={classes.titleSection}>
          <Typography variant="h4" className={classes.title}>Manage Distribution List</Typography>
          <span className={classes.titleBorder}></span>
        </div>
        <div className={classes.actionButtons}>
          <Button
            className={classes.outlinedBtn}
            variant="outlined"
            disableElevation
            onClick={handleOpenUploadOrgDialog}
          >
            Upload BMS/CMO Roles' Access to Events
          </Button>
          <Button
            className={classes.outlinedBtn}
            variant="outlined"
            disableElevation
            onClick={handleDownloadBms}
          >
            Download BMS Roles' Access to Events
          </Button>
          <Button
            className={classes.outlinedBtn}
            variant="outlined"
            disableElevation
            onClick={handleOpenCmoDialog}
          >
            Download CMO Roles' Access to Events
          </Button>
        </div>
      </div>

      {/* ── tabs row with last updated ────────────────────────────────────── */}
      <div className={classes.tabsRow}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          className={classes.tabsRoot}
          classes={{ indicator: classes.tabsIndicator }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {categories.map((cat) => (
            <Tab
              key={cat.label}
              label={`${formatEventName(cat.label)} (${cat.count})`}
              className={classes.tab}
            />
          ))}
        </Tabs>
      </div>

      {/* ── events table ──────────────────────────────────────────────────── */}
      <div className={classes.tableWrapper}>
        <Paper className={classes.tablePaper} elevation={0}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>List of Events</TableCell>
                <TableCell>Last updated by</TableCell>
                <TableCell>Last updated on</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {distributionLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" style={{ padding: '2rem 0' }}>
                    <CircularProgress size={28} style={{ color: ORANGE }} />
                  </TableCell>
                </TableRow>
              ) : filteredDistribution.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" style={{ padding: '2rem 0', color: '#888' }}>
                    No events found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredDistribution.map((ev) => (
                  <TableRow key={ev.eventTypeID} className={classes.tableRow}>
                    <TableCell className={classes.tableCell}>{formatEventName(ev.eventTypeName)}</TableCell>
                    <TableCell className={classes.tableCell}>
                      {ev.lastUpdatedBy ? (
                        <div className={classes.updatedByCell}>
                          <Avatar className={classes.avatar}>
                            {ev.lastUpdatedBy
                              .split(' ')
                              .map((w) => w[0])
                              .join('')
                              .slice(0, 2)
                              .toUpperCase()}
                          </Avatar>
                          {ev.lastUpdatedBy}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {ev.lastUpdatedDate
                        ? new Date(ev.lastUpdatedDate).toLocaleDateString()
                        : null}
                    </TableCell>
                    <TableCell className={classes.actionCell} align="center">
                      <VisibilityIcon
                        fontSize="small"
                        className={classes.actionIconBtn}
                        onClick={() => handleOpenEventDialog(ev, 'view')}
                      />
                      <CreateIcon
                        fontSize="small"
                        className={classes.actionIconBtn}
                        onClick={() => handleOpenEventDialog(ev, 'edit')}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </div>

      {/* ── Event view / edit dialog ──────────────────────────────────────── */}
      <EventEditViewDialog
        open={eventDialogOpen}
        onClose={() => setEventDialogOpen(false)}
        event={selectedEvent}
        mode={eventDialogMode}
        onSave={handleSaveDistribution}
        saving={distributionSaving}
      />

      {/* ── Upload: Step 1 — org type selection ──────────────────────────── */}
      <Dialog
        open={uploadOrgDialogOpen}
        onClose={handleCloseUploadOrgDialog}
        fullWidth
        maxWidth='sm'
        classes={{ paper: classes.uploadDialogPaper }}
      >
        <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
          <div className={classes.cmoDialogCancel}>
            <Button
              className={classes.cancelBtn}
              disableRipple
              endIcon={<CloseIcon className={classes.cancelIcon} />}
              onClick={handleCloseUploadOrgDialog}
            >
              Cancel
            </Button>
          </div>
          <Typography className={classes.cmoDialogTitle}>
            Uploading Roles' Access to Events
          </Typography>
          <Typography className={classes.cmoDialogSubtitle}>
            Please select the Organization Type to proceed.
          </Typography>
          <FormControl variant="outlined" className={classes.cmoFormControl}>
            <Typography className={classes.cmoLabel}>
              Organization Type <span className={classes.requiredAsterisk}>*</span>
            </Typography>
            <Select
              value={selectedOrgType}
              onChange={(e) => setSelectedOrgType(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled><em>Select Organization Type</em></MenuItem>
              <MenuItem value="BMS (Sponsor)">BMS (Sponsor)</MenuItem>
              <MenuItem value="CMO">CMO</MenuItem>
            </Select>
          </FormControl>
          <Button
            className={classes.confirmBtn}
            variant="contained"
            disableElevation
            endIcon={<ChevronRightIcon />}
            disabled={!selectedOrgType}
            onClick={handleConfirmOrgType}
          >
            Confirm Selection
          </Button>
        </DialogContent>
      </Dialog>

      {/* ── Upload: Step 2 — file upload ──────────────────────────────────── */}
      <Dialog
        open={uploadFileDialogOpen}
        onClose={!isUploading ? handleCloseUploadFileDialog : undefined}
        fullWidth
        maxWidth='sm'
        classes={{ paper: classes.uploadDialogPaper }}
      >
        <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
          <div className={classes.cmoDialogCancel}>
            <Button
              className={classes.cancelBtn}
              disableRipple
              endIcon={<CloseIcon className={classes.cancelIcon} />}
              onClick={handleCloseUploadFileDialog}
              disabled={isUploading}
            >
              Cancel
            </Button>
          </div>
          <Typography className={classes.cmoDialogTitle}>
            {uploadDialogTitle}
          </Typography>
          <Typography className={classes.cmoDialogSubtitle}>
            {uploadDialogSubtitle}
          </Typography>

          <Container>
            {selectedOrgType === 'CMO' && (
              <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 16 }}>
                <InputLabel>Select CMO</InputLabel>
                <Select
                  value={selectedUploadCmo}
                  onChange={(e) => {
                    const org = (orgDeptRoles?.organizations || []).find(
                      (o) => o.organization_name === e.target.value
                    );
                    setSelectedUploadCmo(e.target.value);
                    setSelectedUploadCmoId(org?.organization_id || '');
                  }}
                  label="Select CMO"
                  disabled={isUploading}
                  MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                >
                  {(orgDeptRoles?.organizations || [])
                    .filter((o) => o.orgType === 'CMO')
                    .map((o) => (
                      <MenuItem key={o.organization_id || o.organization_name} value={o.organization_name}>
                        {o.organization_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            <Box
              className={classes.uploadBox}
              onClick={handleUploadBoxClick}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <input
                type="file"
                hidden
                ref={fileInputRef}
                accept=".xlsx"
                onChange={handleFileChange}
              />
              <Typography className={classes.uploadText}>
                Drag & drop file or click here to upload
              </Typography>
              <PublishIcon className={classes.uploadIcon} />
            </Box>

            <Box className={classes.file}>
              <Typography>Select file type:</Typography>
              <FormControl variant="outlined" size="small">
                <Select
                  value={fileType}
                  onChange={(e) => setFileType(e.target.value)}
                  disabled={uploadFile !== null}
                >
                  <MenuItem value=".xlsx">.xlsx</MenuItem>
                </Select>
              </FormControl>

              {uploadFile && (
                <Box className={classes.linkBox}>
                  <Typography className={classes.link}>
                    {uploadFile.name}
                  </Typography>
                  <span onClick={handleRemoveFile} style={{ cursor: 'pointer' }}>
                    ❌
                  </span>
                </Box>
              )}

              <Box>
                <Typography className={classes.bold}>
                  Validation criteria
                </Typography>
                <Typography className={classes.criteria}>
                  Only .xlsx files are accepted
                </Typography>
                <Typography>First row must contain headers only</Typography>
                <Typography>
                  File should not contain blank rows or columns
                </Typography>
              </Box>

              {uploadError && (
                <Typography style={{ color: '#d32f2f', fontSize: '0.85rem', marginBottom: 8, marginTop: 4 }}>
                  {uploadError}
                </Typography>
              )}

              <Button
                variant="contained"
                className={classes.uploadBtn}
                endIcon={<ChevronRightIcon />}
                onClick={handleUpload}
                disabled={!uploadFile || isUploading || (selectedOrgType === 'CMO' && !selectedUploadCmo)}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </Box>
          </Container>
        </DialogContent>

        <Backdrop className={classes.backdrop} open={isUploading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Dialog>

      {/* ── Validation Summary Dialog ──────────────────────────────────────── */}
      <Dialog
        open={validationDialogOpen}
        onClose={() => !isSubmitting && setValidationDialogOpen(false)}
        fullWidth
        maxWidth='sm'
        classes={{ paper: classes.validationDialogPaper }}
      >
        <DialogContent className={classes.validationDialogContent}>
          <Typography className={classes.validationTitle}>
            Validation Summary
          </Typography>
          <Typography className={classes.validationSubtitle}>
            {validationData?.message || 'Review the validation results below'}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Box className={classes.summaryBox}>
                <Typography className={classes.summaryNumber}>
                  {validationData?.validRecordsCount + validationData?.invalidRecordsCount || 0}
                </Typography>
                <Typography className={classes.summaryLabel}>Total Records</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box className={classes.summaryBox} style={{ borderColor: '#4caf50' }}>
                <Typography className={classes.summaryNumber} style={{ color: '#4caf50' }}>
                  {validationData?.validRecordsCount || 0}
                </Typography>
                <Typography className={classes.summaryLabel}>Valid Records</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box className={classes.summaryBox} style={{ borderColor: '#f44336' }}>
                <Typography className={classes.summaryNumber} style={{ color: '#f44336' }}>
                  {validationData?.invalidRecordsCount || 0}
                </Typography>
                <Typography className={classes.summaryLabel}>Invalid Records</Typography>
              </Box>
            </Grid>
          </Grid>

          <Box className={classes.validationActions}>
            <Button
              className={classes.reUploadBtn}
              onClick={handleReUpload}
              disabled={isSubmitting}
            >
              Re-Upload File
            </Button>
            <Button
              className={classes.submitBtn}
              variant="contained"
              disableElevation
              disabled={!validationData?.validRecordsCount || isSubmitting}
              onClick={handleSubmitValidRecords}
              endIcon={isSubmitting ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <ChevronRightIcon />}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Valid Records'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Submission success dialog ─────────────────────────────────────── */}
      <Dialog
        open={submissionSuccess}
        onClose={handleCloseSubmissionSuccess}
        fullWidth
        maxWidth='xs'
        classes={{ paper: classes.successDialogPaper }}
      >
        <DialogContent className={classes.successContent}>
          <CheckCircleIcon className={classes.successIcon} />
          <Typography className={classes.successTitle}>
            Submitted Successfully!
          </Typography>
          <Typography className={classes.successSubtitle}>
            {uploadDialogTitle} has been submitted successfully.
          </Typography>
          <Button
            className={classes.okBtn}
            variant="contained"
            disableElevation
            onClick={handleCloseSubmissionSuccess}
          >
            Ok
          </Button>
        </DialogContent>
      </Dialog>

      {/* ── CMO selection dialog ───────────────────────────────────────────── */}
      <Dialog
        open={cmoDialogOpen}
        onClose={handleCloseCmoDialog}
        fullWidth
        maxWidth='sm'
        classes={{ paper: classes.cmoDialogPaper }}
      >
        <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
          {/* cancel row */}
          <div className={classes.cmoDialogCancel}>
            <Button
              className={classes.cancelBtn}
              disableRipple
              endIcon={<CloseIcon className={classes.cancelIcon} />}
              onClick={handleCloseCmoDialog}
            >
              Cancel
            </Button>
          </div>

          {/* title */}
          <Typography className={classes.cmoDialogTitle}>
            Downloading CMO Roles' Access to Events
          </Typography>

          {/* subtitle */}
          <Typography className={classes.cmoDialogSubtitle}>
            Please select the CMO Name to proceed
          </Typography>

          {/* CMO Name select */}
          <FormControl variant="outlined" className={classes.cmoFormControl}>
            <Typography className={classes.cmoLabel}>
              CMO Name <span className={classes.requiredAsterisk}>*</span>
            </Typography>
            <Select
              value={selectedCmo}
              onChange={(e) => setSelectedCmo(e.target.value)}
              displayEmpty
              MenuProps={{ PaperProps: { style: { maxHeight: 250 } } }}
            >
              <MenuItem value="" disabled>
                <em>Select CMO</em>
              </MenuItem>
              {cmoOptions.map((cmo) => (
                <MenuItem key={cmo.id} value={cmo.id}>{cmo.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* confirm button */}
          <Button
            className={classes.confirmBtn}
            variant="contained"
            disableElevation
            endIcon={<ChevronRightIcon />}
            disabled={!selectedCmo}
            onClick={handleConfirmDownload}
          >
            Confirm Selection
          </Button>
        </DialogContent>
      </Dialog>

      {/* ── Download loading backdrop ─────────────────────────────────────── */}
      <Backdrop open={downloadLoading} style={{ zIndex: 1400, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* ── Excel preview dialog ──────────────────────────────────────────── */}
      <Dialog
        open={excelPreviewOpen}
        onClose={handleCloseExcelPreview}
        fullWidth
        maxWidth="lg"
        PaperProps={{ style: { borderRadius: 14 } }}
      >
        <DialogContent style={{ padding: 24 }}>
          {/* header row */}
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Typography
              style={{ fontSize: '1rem', fontWeight: 500, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={handleCloseExcelPreview}
            >
              Close <CloseIcon style={{ marginLeft: 4, color: '#FF0000' }} />
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center">
              <img
                src="https://img.icons8.com/color/96/000000/microsoft-excel-2019--v1.png"
                alt="Excel"
                style={{ width: 48, height: 48, marginRight: 16 }}
              />
              <Box>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  Event Role Mapping
                </Typography>
                <Typography variant="body2" style={{ color: '#666' }}>
                  Excel Spreadsheet Preview
                </Typography>
              </Box>
            </Box>
            {excelFileURL && (
              <Button
                variant="contained"
                startIcon={<CloudDownloadIcon />}
                href={excelFileURL}
                download={excelFileName}
                style={{ textTransform: 'none', backgroundColor: ORANGE, color: '#fff', borderRadius: '2rem' }}
              >
                Download
              </Button>
            )}
          </Box>

          {/* table preview */}
          <div style={{ overflowX: 'auto', maxHeight: 450, border: '1px solid #e0e0e0', borderRadius: 4 }}>
            <Table size="small" stickyHeader>
              {excelData.length > 0 && (
                <TableHead>
                  <TableRow>
                    {excelData[0].map((header, j) => (
                      <TableCell
                        key={j}
                        style={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          backgroundColor: '#4472C4',
                          color: '#fff',
                          position: 'sticky',
                          top: 0,
                          zIndex: j === 0 ? 3 : 2,
                          ...(j === 0 ? { left: 0, position: 'sticky' } : {}),
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {excelData.slice(1).map((row, i) => (
                  <TableRow key={i} hover>
                    {(row || []).map((cell, j) => (
                      <TableCell
                        key={j}
                        style={{
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          backgroundColor: j === 0 ? '#f5f5f5' : undefined,
                          ...(j === 0 ? { position: 'sticky', left: 0, zIndex: 1 } : {}),
                          color: cell === 'Yes' ? '#2e7d32' : cell === 'No' ? '#c62828' : '#333',
                          fontWeight: cell === 'Yes' || cell === 'No' ? 600 : 400,
                        }}
                      >
                        {cell != null ? String(cell) : ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Success/Error toast for distribution save ───────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

    </div>
  );
};

export default ManageDistributionList;
